import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Checkbox,
  DatePicker,
  Button,
  Row,
  Col,
  Typography,
  message, Modal, Spin
} from "antd";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { ArrowLeftOutlined, EditOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import notificationMobileApi, { NotificationMobilePayload } from "../../services/notificationMobile";
import CustomUploadAdapter from "./CustomUploadAdapter";
import MobileMobilePreviewPage from "./MobileMobilePreview";
import stylesCheckbox from "./Notification.module.css";
import TimeZone from "../../services/timeZone";
import { validateTextField } from "../../utils/validators";

// ============================================
// HELPER FUNCTION TO EXTRACT IMAGE URLs
// ============================================
const extractImageUrls = (htmlContent: string): string[] => {
  if (!htmlContent) return [];
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const images = doc.querySelectorAll('img');
    return Array.from(images).map(img => img.src);
  } catch (error) {
    console.error('Error extracting image URLs:', error);
    return [];
  }
};

const NotificationCreateMobile = () => {
  const [form] = Form.useForm();
  const [previewData, setPreviewData] = useState<any>({
    campaignName: "",
    startDate: null,
    endDate: null,
    notificationType: [],
    title: "",
    subtitle: "",
    description: "",
    emailSubject: "",
    emailText: "",
  });
  const [previewVisible, setPreviewVisible] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [editorData, setEditorData] = useState<string>("");
  const [notificationData, setNotificationData] = useState<NotificationMobilePayload | null>(null);

  const navigate = useNavigate();
  const { id, mode: urlMode } = useParams<{ id?: string; mode: string }>();
  const [mode, setMode] = useState<"create" | "edit" | "view" | "views">(urlMode as any);

  const [messageApi, contextHolder] = message.useMessage();

  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);

  const [loading, setLoading] = useState(false); // ✅ loader state

  const [editorInstance, setEditorInstance] = useState<any>(null);

  // Toggle CKEditor read-only mode when mode changes
  useEffect(() => {
    if (editorInstance) {
      if (mode === "view" || mode === "views") {
        editorInstance.enableReadOnlyMode("viewMode");
      } else {
        editorInstance.disableReadOnlyMode("viewMode");
      }
    }
  }, [mode, editorInstance]);

  const processDescriptionImages = (description: string, images: any[]) => {
    if (!description || !images || images.length === 0) {
      //console.log('No description or images to process');
      return description;
    }

    let processed = description;

    // Extract all blob URLs from the description
    const blobRegex = /blob:[^"'\s]+/g;
    const blobUrls = description.match(blobRegex) || [];

    // Match by comparing the order of blob URLs with images that don't already have real URLs in description
    let imageIndex = 0;
    blobUrls.forEach((blobUrl) => {
      // Find the next image that isn't already in the description with a real URL
      while (imageIndex < images.length && processed.includes(images[imageIndex].fileUrl)) {
        imageIndex++;
      }

      if (imageIndex < images.length) {
        const actualUrl = images[imageIndex].fileUrl;
        //console.log(`Replacing ${blobUrl} with ${actualUrl}`);
        processed = processed.replace(blobUrl, actualUrl);
        imageIndex++;
      }
    });

    return processed;
  };

  // Reset form when mode changes to create
  useEffect(() => {
    if (mode === "create") {
      setNotificationData(null);
      form.setFieldsValue({
        campaignName: "",
        startDate: null,
        endDate: null,
        notificationType: [],
        title: "",
        subtitle: "",
        emailSubject: "",
        emailText: "",
        description: "",
      });
      setSelectedTypes([]);
      setEditorData("");
      setPreviewData({
        campaignName: "",
        startDate: null,
        endDate: null,
        notificationType: [],
        title: "",
        subtitle: "",
        description: "",
        emailSubject: "",
        emailText: "",
      });

      // Clear all window storage
      (window as any).serverImages = [];
      (window as any).pendingContestImages = [];
      (window as any).blobUrlMappings = []; // ✅ ADD THIS
      (window as any).currentEditorContent = "";

    }
  }, [mode, form]);

  // Fetch notification by ID if editing/viewing
  useEffect(() => {
    if (id) {
      notificationMobileApi.getNotificationById(id)
        .then((data) => setNotificationData(data))
        .catch((err) => console.error(err));
    }
  }, [id]);

  // Populate form when notificationData is available
  useEffect(() => {
    if (notificationData) {
      const images = (notificationData as any).announcementImageResponses || [];
      let processedDescription = notificationData.description ?? "";
      // Only process if description contains blob URLs (shouldn't happen when loading from DB)
      if (processedDescription && processedDescription.includes('blob:')) {
        processedDescription = processDescriptionImages(
          notificationData.description,
          images
        );
      }

      let startDateTime = null;
      let endDateTime = null;
      if (notificationData.startDate && notificationData.startTime) {
        const startStr = `${notificationData.startDate} ${notificationData.startTime}`;
        startDateTime = dayjs(startStr, "YYYY-MM-DD h:mm A");
      }

      if (notificationData.endDate && notificationData.endTime) {
        const endStr = `${notificationData.endDate} ${notificationData.endTime}`;
        endDateTime = dayjs(endStr, "YYYY-MM-DD h:mm A");
      }

      form.setFieldsValue({
        campaignName: notificationData.campaignName,
        startDate: startDateTime && startDateTime.isValid() ? startDateTime : null,
        endDate: endDateTime && endDateTime.isValid() ? endDateTime : null,
        notificationType: [
          notificationData.inAppNotification ? "inApp" : null,
          notificationData.pushNotification ? "push" : null,
          notificationData.emailNotification ? "email" : null,
        ].filter(Boolean),
        title: notificationData.title,
        subtitle: notificationData.subTitle,
        emailSubject: notificationData.subjectLine,
        emailText: notificationData.emailText,
      });
      setEditorData(processedDescription);
      setPreviewData({
        campaignName: notificationData.campaignName,
        startDate: startDateTime,
        endDate: endDateTime,
        title: notificationData.title,
        notificationType: [
          notificationData.emailNotification ? "email" : null,
          notificationData.inAppNotification ? "inApp" : null,
          notificationData.pushNotification ? "push" : null, // example third type
        ].filter(Boolean), // removes nulls
        subtitle: notificationData.subTitle,
        description: processedDescription,
        emailSubject: notificationData.subjectLine,
        emailText: notificationData.emailText,
      });

      const types = [];
      if (notificationData.inAppNotification) types.push("inApp");
      if (notificationData.pushNotification) types.push("push");
      if (notificationData.emailNotification) types.push("email");
      setSelectedTypes(types);

      // Store server images with ACTUAL sizes
      (window as any).serverImages = images.map((img: any) => ({
        id: img.id,
        fileName: img.fileName,
        fileUrl: img.fileUrl,
        fileSize: img.fileSize || 0 // ✅ Use actual size
      }));

      // CRITICAL: Clear pending images and mappings
      (window as any).pendingContestImages = [];
      (window as any).blobUrlMappings = []; // ✅ ADD THIS
      (window as any).currentEditorContent = processedDescription;
    }
  }, [notificationData, form]);

  const handleFormChange = (_: any, allValues: any) => {
    setPreviewData((prev: any) => ({
      ...prev,
      campaignName: allValues.campaignName || "",
      startDate: allValues.startDate || null,
      endDate: allValues.endDate || null,
      notificationType: allValues.notificationType || [],
      title: allValues.title || "",
      subtitle: allValues.subtitle || "",
      emailSubject: allValues.emailSubject || "",
      emailText: allValues.emailText || "",
    }));
  };

  const handleTypeChange = (values: string[]) => setSelectedTypes(values);

  const handleDescriptionChange = (_: any, editor: any) => {
    const data = editor.getData();

    //  CRITICAL: Update current editor content for validation
    (window as any).currentEditorContent = data;


    //  Update editorData state FIRST (this makes images visible)
    setEditorData(data);

    // --- Track deleted images in edit mode ---
    if (mode === "edit" && (window as any).serverImages) {
      const currentUrls = extractImageUrls(data);
      const previousUrls = extractImageUrls(editorData);

      // Find URLs that were removed
      const deletedUrls = previousUrls.filter(url => !currentUrls.includes(url));

      if (deletedUrls.length > 0) {
        console.log('Images removed from editor:', deletedUrls);

        // Remove deleted images from serverImages array
        (window as any).serverImages = (window as any).serverImages.filter(
          (img: any) => !deletedUrls.includes(img.fileUrl)
        );

        // Also remove from pending images if they exist
        const blobMappings = (window as any).blobUrlMappings || [];
        const pendingImages = (window as any).pendingContestImages || [];

        const updatedPending: File[] = [];
        const updatedMappings: any[] = [];

        pendingImages.forEach((file: File, index: number) => {
          const mapping = blobMappings[index];
          if (mapping && !deletedUrls.includes(mapping.blobUrl)) {
            updatedPending.push(file);
            updatedMappings.push(mapping);
          }
        });

        (window as any).pendingContestImages = updatedPending;
        (window as any).blobUrlMappings = updatedMappings;

        //console.log('Updated pending images:', updatedPending.length);
        //console.log('Updated server images:', (window as any).serverImages.length);
      }
    }

    // Force set field value & trigger validation
    form.setFieldsValue({ description: data });
    form.validateFields(['description']).catch(() => { });

    setPreviewData((prev: any) => ({ ...prev, description: data }));
  };




  const disabledPastDates = (current: Dayjs) => current && current < dayjs().startOf("day");

  const disabledPastTimes = (date: Dayjs | null, type: "start" | "end") => {
    if (!date) return {};

    if (date.isSame(dayjs(), "day") && type === "start") {
      const now = dayjs().add(15, "minute"); // ✅ add 15 minutes safely
      const currentHour = now.hour();
      const currentMinute = now.minute();

      return {
        disabledHours: () =>
          Array.from({ length: 24 }, (_, i) => i).filter((h) => h < currentHour),
        disabledMinutes: (selectedHour: number) =>
          selectedHour === currentHour
            ? Array.from({ length: 60 }, (_, i) => i).filter((m) => m < currentMinute)
            : [],
      };
    }

    return {};
  };

  const disabledEndDate = (current: Dayjs) => {
    const startDate: Dayjs = form.getFieldValue("startDate");
    if (!startDate) return current && current < dayjs().startOf("day");
    return current && current.isBefore(startDate, "day");
  };

  const disabledEndTime = (date: Dayjs | null) => {
    const startDate: Dayjs = form.getFieldValue("startDate");
    if (!date || !startDate) return {};
    if (date.isSame(startDate, "day")) {
      const startHour = startDate.hour();
      const startMinute = startDate.minute();
      return {
        disabledHours: () => Array.from({ length: 24 }, (_, i) => i).filter(h => h < startHour),
        disabledMinutes: (selectedHour: number) =>
          selectedHour === startHour ? Array.from({ length: 60 }, (_, i) => i).filter(m => m < startMinute) : [],
      };
    }
    return {};
  };

  function replaceBlobUrlsWithActualUrls(
    html: string,
    mappings: { blobUrl: string; actualUrl: string, id: string }[]
  ) {
    if (!html || !mappings.length) return html;
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const images = doc.querySelectorAll("img");

    images.forEach((img) => {
      let src = img.getAttribute("src")?.trim();
      if (!src) return;

      const mapping = mappings.find((m) => {
        // remove any quotes around blobUrl
        const cleanBlob = m.blobUrl.replace(/["']/g, "").trim();
        return src === cleanBlob || src === `"${cleanBlob}"`;
      });

      if (mapping) {
        //  Strip quotes from actualUrl
        const cleanActual = mapping.actualUrl.replace(/["']/g, "").trim();
        img.setAttribute("src", cleanActual);

        // Add image ID attribute to HTML tag
        img.setAttribute("data-imgid", mapping.id);
      }
    });

    return doc.body.innerHTML;
  }

  const handleSave = async (status: number) => {
    try {
      setLoading(true);
      //  DRAFT MODE (status === 0) - Only validate Campaign Name
      if (status === 0) {
        try {
          await form.validateFields(['campaignName']);
        } catch (error) {
          messageApi.error("Please enter Campaign Name before saving as draft.");
          setLoading(false);
          return;
        }

        // Get current form values (without full validation)
        const values = form.getFieldsValue();
        const start = values.startDate;
        const end = values.endDate;

        if (start && dayjs(start).isBefore(dayjs())) {
          messageApi.error("Start date & time cannot be earlier than the current time!");
          setLoading(false);
          return;
        }

        // Get description from editor
        const descriptionValue = (values.description?.trim() || editorData?.trim() || "");

        let finalDescription = editorData;

        if (mode === "edit" && notificationData?.id) {
          // ===== EDIT MODE - DRAFT =====
          const currentDescription = editorData;
          const currentUrls = extractImageUrls(currentDescription);
          const serverImages = (window as any).serverImages || [];

          //  FIX: Only keep server images that are still in the current editor content
          const remainingServerImageIds = serverImages
            .filter((img: any) => currentUrls.includes(img.fileUrl))
            .map((img: any) => img.id);

          const pendingImages: File[] = (window as any).pendingContestImages || [];
          const blobMappings = (window as any).blobUrlMappings || [];
          const uploadedMappings: { id: string, blobUrl: string; actualUrl: string }[] = [];

          //  FIX: Only upload pending images that are still in the editor
          if (pendingImages.length > 0) {
            for (let i = 0; i < pendingImages.length; i++) {
              const file = pendingImages[i];
              const mapping = blobMappings[i];

              // Check if this blob URL still exists in current editor content
              if (mapping?.blobUrl && currentDescription.includes(mapping.blobUrl)) {
                const uploadResponse = await notificationMobileApi.uploadImage(notificationData.id, file);
                const actualUrl = uploadResponse?.fileUrl || uploadResponse?.data?.fileUrl;

                if (actualUrl) {
                  uploadedMappings.push({
                    blobUrl: mapping.blobUrl,
                    actualUrl: actualUrl,
                    id: uploadResponse.id
                  });
                }
              }
            }
          }

          finalDescription = replaceBlobUrlsWithActualUrls(finalDescription, uploadedMappings);
          finalDescription = finalDescription.replace(/\\"/g, '"');
          finalDescription = finalDescription.replaceAll('.png22%', '.png');

          const allImageIds = [
            ...remainingServerImageIds,
            ...uploadedMappings.map(m => m.id)
          ];

          const updatepayload: any = {
            campaignName: values.campaignName,
            status: 0,
            startDate: start ? dayjs(start).format("YYYY-MM-DD") : null,
            startTime: start ? dayjs(start).format("hh:mm A") : null,
            endDate: end ? dayjs(end).format("YYYY-MM-DD") : null,
            endTime: end ? dayjs(end).format("hh:mm A") : null,
            inAppNotification: values.notificationType?.includes("inApp") || null,
            pushNotification: values.notificationType?.includes("push") || null,
            emailNotification: values.notificationType?.includes("email") || null,
            title: values.title || null,
            subTitle: values.subtitle || null,
            subjectLine: values.emailSubject || null,
            emailText: values.emailText || null,
            description: finalDescription || null,
            announcementImagesIDs: allImageIds,
            timezone: TimeZone.getFriendlyTimeZone(),
            id: notificationData.id
          };

          try {
            await notificationMobileApi.updateNotification(updatepayload);
          } catch (err: any) {
            const backendMessage =
              err?.response?.data?.Message ||
              err?.response?.data?.message ||
              "Failed to save as draft.";
            messageApi.error(backendMessage);
            setLoading(false);
            return;
          }
        } else {
          // ===== CREATE MODE - DRAFT =====
          const payload: any = {
            campaignName: values.campaignName,
            status: 0,
            startDate: start ? dayjs(start).format("YYYY-MM-DD") : null,
            startTime: start ? dayjs(start).format("hh:mm A") : null,
            endDate: end ? dayjs(end).format("YYYY-MM-DD") : null,
            endTime: end ? dayjs(end).format("hh:mm A") : null,
            inAppNotification: values.notificationType?.includes("inApp") || null,
            pushNotification: values.notificationType?.includes("push") || null,
            emailNotification: values.notificationType?.includes("email") || null,
            title: values.title || null,
            subTitle: values.subtitle || null,
            subjectLine: values.emailSubject || null,
            emailText: values.emailText || null,
            description: editorData || null,
            timezone: TimeZone.getFriendlyTimeZone()
          };

          let announcementSupportId = "";
          try {
            const response = await notificationMobileApi.saveNotification(payload);
            if (!response?.id)
              throw new Error("No AnnouncementSupportID returned!");
            announcementSupportId = response.id;
          } catch (err: any) {
            const backendMessage =
              err?.response?.data?.Message ||
              err?.response?.data?.message ||
              "Failed to save as draft.";
            messageApi.error(backendMessage);
            setLoading(false);
            return;
          }

          const pendingImages: File[] = (window as any).pendingContestImages || [];
          const blobMappings = (window as any).blobUrlMappings || [];
          const uploadedMappings: { blobUrl: string; actualUrl: string, id: string }[] = [];

          //  FIX: Only upload images that are still in the editor
          if (pendingImages.length > 0) {
            for (let i = 0; i < pendingImages.length; i++) {
              const file = pendingImages[i];
              const mapping = blobMappings[i];

              // Check if this blob URL still exists in current editor content
              if (mapping?.blobUrl && editorData.includes(mapping.blobUrl)) {
                const uploadResponse = await notificationMobileApi.uploadImage(announcementSupportId, file);
                const actualUrl = uploadResponse?.fileUrl || uploadResponse?.data?.fileUrl;

                if (actualUrl) {
                  uploadedMappings.push({
                    blobUrl: mapping.blobUrl,
                    actualUrl: actualUrl,
                    id: uploadResponse.id
                  });
                }
              }
            }
          }

          let finalDescription = replaceBlobUrlsWithActualUrls(editorData, uploadedMappings);
          const updateDescriptionPayload: any = {
            id: announcementSupportId,
            description: finalDescription?.trim() || ""
          };
          await notificationMobileApi.updateFinalDescriptionNotification(updateDescriptionPayload);
        }

        messageApi.success("Campaign saved as draft successfully!");
        setEditorData("");
        form.resetFields();
        setTimeout(() => {
          navigate(-1);
        }, 1000);
        return;
      }

      //  PUBLISH MODE (status === 1) - Validate ALL fields
      const values = await form.validateFields();
      const start = values.startDate;
      const end = values.endDate;

      if (start && dayjs(start).isBefore(dayjs())) {
        messageApi.error("Start date & time cannot be earlier than the current time!");
        setLoading(false);
        return;
      }

      // Validate description
      const descriptionValue = (values.description?.trim() || editorData?.trim() || "");
      const plainDescription = descriptionValue.replace(/<[^>]*>/g, "").trim();

      if (!plainDescription) {
        messageApi.error("Please enter notification description!");
        setLoading(false);
        return;
      }

      // Validation: In-App must have both start & end
      if (selectedTypes.includes("inApp") && !end) {
        messageApi.error("End date & time is required for In-App notifications!");
        setLoading(false);
        return;
      }

      let finalDescription = editorData;

      if (mode === "edit" && notificationData?.id) {
        // ===== EDIT MODE - PUBLISH =====
        const currentDescription = editorData;
        const currentUrls = extractImageUrls(currentDescription);
        const serverImages = (window as any).serverImages || [];

        //  FIX: Only keep server images that are still in the current editor content
        const remainingServerImageIds = serverImages
          .filter((img: any) => currentUrls.includes(img.fileUrl))
          .map((img: any) => img.id);

        const pendingImages: File[] = (window as any).pendingContestImages || [];
        const blobMappings = (window as any).blobUrlMappings || [];
        const uploadedMappings: { id: string, blobUrl: string; actualUrl: string }[] = [];

        //  FIX: Only upload pending images that are still in the editor
        if (pendingImages.length > 0) {
          for (let i = 0; i < pendingImages.length; i++) {
            const file = pendingImages[i];
            const mapping = blobMappings[i];

            // Check if this blob URL still exists in current editor content
            if (mapping?.blobUrl && currentDescription.includes(mapping.blobUrl)) {
              const uploadResponse = await notificationMobileApi.uploadImage(notificationData.id, file);
              const actualUrl = uploadResponse?.fileUrl || uploadResponse?.data?.fileUrl;

              if (actualUrl) {
                uploadedMappings.push({
                  blobUrl: mapping.blobUrl,
                  actualUrl: actualUrl,
                  id: uploadResponse.id
                });
              }
            }
          }
        }

        finalDescription = replaceBlobUrlsWithActualUrls(finalDescription, uploadedMappings);
        finalDescription = finalDescription.replace(/\\"/g, '"');
        finalDescription = finalDescription.replaceAll('.png22%', '.png');

        const allImageIds = [
          ...remainingServerImageIds,
          ...uploadedMappings.map(m => m.id)
        ];

        const updatepayload: any = {
          campaignName: values.campaignName,
          status: 1,
          startDate: start ? dayjs(start).format("YYYY-MM-DD") : null,
          startTime: start ? dayjs(start).format("hh:mm A") : null,
          endDate: end ? dayjs(end).format("YYYY-MM-DD") : null,
          endTime: end ? dayjs(end).format("hh:mm A") : null,
          inAppNotification: values.notificationType?.includes("inApp") || null,
          pushNotification: values.notificationType?.includes("push") || null,
          emailNotification: values.notificationType?.includes("email") || null,
          title: values.title,
          subTitle: values.subtitle,
          subjectLine: values.emailSubject || null,
          emailText: values.emailText || null,
          description: finalDescription,
          announcementImagesIDs: allImageIds,
          timezone: TimeZone.getFriendlyTimeZone(),
          id: notificationData.id
        };

        try {
          await notificationMobileApi.updateNotification(updatepayload);
        } catch (err: any) {
          const backendMessage =
            err?.response?.data?.Message ||
            err?.response?.data?.message ||
            "Failed to save as publish.";
          messageApi.error(backendMessage);
          setLoading(false);
          return;
        }
      } else {
        // ===== CREATE MODE - PUBLISH =====
        const payload: any = {
          campaignName: values.campaignName,
          status: 1,
          startDate: start ? dayjs(start).format("YYYY-MM-DD") : null,
          startTime: start ? dayjs(start).format("hh:mm A") : null,
          endDate: end ? dayjs(end).format("YYYY-MM-DD") : null,
          endTime: end ? dayjs(end).format("hh:mm A") : null,
          inAppNotification: values.notificationType?.includes("inApp") || null,
          pushNotification: values.notificationType?.includes("push") || null,
          emailNotification: values.notificationType?.includes("email") || null,
          title: values.title,
          subTitle: values.subtitle,
          subjectLine: values.emailSubject || null,
          emailText: values.emailText || null,
          description: editorData,
          timezone: TimeZone.getFriendlyTimeZone()
        };

        let announcementSupportId = "";
        try {
          const response = await notificationMobileApi.saveNotification(payload);
          if (!response?.id)
            throw new Error("No AnnouncementSupportID returned!");
          announcementSupportId = response.id;
        } catch (err: any) {
          const backendMessage =
            err?.response?.data?.Message ||
            err?.response?.data?.message ||
            "Failed to save as publish.";
          messageApi.error(backendMessage);
          setLoading(false);
          return;
        }

        const pendingImages: File[] = (window as any).pendingContestImages || [];
        const blobMappings = (window as any).blobUrlMappings || [];
        const uploadedMappings: { blobUrl: string; actualUrl: string, id: string }[] = [];

        //  FIX: Only upload images that are still in the editor
        if (pendingImages.length > 0) {
          for (let i = 0; i < pendingImages.length; i++) {
            const file = pendingImages[i];
            const mapping = blobMappings[i];

            // Check if this blob URL still exists in current editor content
            if (mapping?.blobUrl && editorData.includes(mapping.blobUrl)) {
              const uploadResponse = await notificationMobileApi.uploadImage(announcementSupportId, file);
              const actualUrl = uploadResponse?.fileUrl || uploadResponse?.data?.fileUrl;

              if (actualUrl) {
                uploadedMappings.push({
                  blobUrl: mapping.blobUrl,
                  actualUrl: actualUrl,
                  id: uploadResponse.id
                });
              }
            }
          }
        }

        let finalDescription = replaceBlobUrlsWithActualUrls(editorData, uploadedMappings);
        const updateDescriptionPayload: any = {
          id: announcementSupportId,
          description: finalDescription?.trim() || ""
        };
        await notificationMobileApi.updateFinalDescriptionNotification(updateDescriptionPayload);
      }

      messageApi.success("Notification scheduled successfully.");
      setEditorData("");
      form.resetFields();
      setTimeout(() => {
        navigate(-1);
      }, 1000);

    } catch (err: any) {
      console.error('Save error:', err);
      //  FIX: Prevent double error messages
      if (!messageApi?.destroy) {
        messageApi.error("Please fix validation errors before saving.");
      }
    } finally {
      setLoading(false);
      setIsPublishModalOpen(false);
    }
  };

  const openPreview = () => {
    setPreviewData((prev) => ({
      ...prev,
      campaignName: form.getFieldValue("campaignName") || "",
      startDate: form.getFieldValue("startDate") || null,
      endDate: form.getFieldValue("endDate") || null,
      notificationType: form.getFieldValue("notificationType") || [],
      title: form.getFieldValue("title") || "",
      subtitle: form.getFieldValue("subtitle") || "",
      description: form.getFieldValue("description") || "",
      emailSubject: form.getFieldValue("emailSubject") || "",
      emailText: form.getFieldValue("emailText") || "",
    }));
    setPreviewVisible(true);
  };

  const pageTitle =
    mode === undefined || mode === "create"
      ? "Add Notification"
      : mode === "edit"
        ? "Edit Notification"
        : "View Notification";

  return (
    <>
      {contextHolder}

      <div style={{ position: "relative" }}>
        {/* ✅ Loader Overlay */}
        {loading && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(255, 255, 255, 0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 2000,
            }}
          >
            <img
              src="/images/newLoading.gif" // <-- path to your GIF
              alt="Loading..."
              style={{ width: 200, height: 150 }}
            />
          </div>
        )}
      </div>

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Typography.Title level={3} style={{ margin: 0, color: "#41787C" }}>
            <Button
              type="link"
              icon={<ArrowLeftOutlined style={{ fontSize: 18, fontWeight: "bold", color: "#41787C", marginRight: 10 }} />}
              onClick={() => navigate(-1)}
              style={{ marginBottom: 10, padding: 0 }}
            />
            {pageTitle}
          </Typography.Title>
        </Col>
        {mode === "view" && (
          <Col>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => setMode("edit")}
              className="submit-btn btn-publish"
            >
              Edit
            </Button>
          </Col>
        )}
      </Row>

      <Form form={form} layout="vertical" onValuesChange={handleFormChange} requiredMark={false} >
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={24} md={12}>
            <Form.Item
              label={
                <span style={styles.campaignLabel}>
                  Campaign Name
                  <span style={{ color: "red", marginLeft: 4 }}>*</span>
                </span>
              }
              name="campaignName"
              rules={[
                { validator: validateTextField("Campaign Name", 100, true) },
              ]}
            >
              <Input
                placeholder="Enter Campaign Name"
                disabled={mode === "view" || mode === "views"}
                style={styles.input}
                onPaste={(e) => {
                  const pastedText = e.clipboardData.getData("text");
                  const currentValue = (e.target as HTMLInputElement).value;
                  if (currentValue.length + pastedText.length > 100) {
                    e.preventDefault(); // prevent paste
                    message.error("Cannot paste more than 100 characters");
                  }
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={24} md={12}>
            <Form.Item
              label={<span style={styles.campaignLabel}>Campaign Duration<span style={{ color: "red", marginLeft: 4 }}>*</span></span>}
              style={{ marginBottom: 0 }}
            >
              <Row gutter={[8, 8]} wrap={false}>
                <Col xs={24} sm={12} style={{ flex: 1 }}>
                  <Form.Item
                    name="startDate"
                    rules={[{ required: true, message: "Start date & time is required!" }]}
                    noStyle
                  >
                    <DatePicker
                      showTime={{ format: "hh:mm A" }}
                      format="MM/DD/YYYY hh:mm A"
                      placeholder="Start Date & Time"
                      style={{ width: "100%", height: 43 }}
                      disabledDate={disabledPastDates}
                      disabled={mode === "view" || mode === "views"}
                      disabledTime={(date) => disabledPastTimes(date, "start")}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} style={{ flex: 1 }}>
                  <Form.Item
                    name="endDate"
                    rules={[
                      {
                        validator: (_, value) => {
                          const startDate = form.getFieldValue("startDate");
                          if (selectedTypes.includes("inApp") && !value) {
                            return Promise.reject(
                              new Error("End date & time is required for In-App notifications!")
                            );
                          }
                          if (startDate && value && value.isBefore(startDate)) {
                            return Promise.reject(
                              new Error("End date & time cannot be before start date!")
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                    noStyle
                  >
                    <DatePicker
                      showTime={{ format: "hh:mm A" }}
                      format="MM/DD/YYYY hh:mm A"
                      placeholder="End Date & Time"
                      style={{ width: "100%", height: 43 }}
                      disabled={mode === "view" || mode === "views"}
                      disabledDate={disabledEndDate}
                      disabledTime={disabledEndTime}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={24}>
            <Form.Item
              label={<span style={styles.campaignLabel}>Notification Type<span style={{ color: "red", marginLeft: 4 }}>*</span></span>}
              name="notificationType"
              rules={[{ required: true, message: "Select at least one notification type!" }]}
            >
              <Checkbox.Group
                className={stylesCheckbox.checkboxGroup}
                onChange={handleTypeChange}
                disabled={mode === "view" || mode === "views"}
              >
                <Checkbox value="inApp">In-App Notification</Checkbox>
                <Checkbox value="push">Push Notification</Checkbox>
                <Checkbox value="email">Email Notification</Checkbox>
              </Checkbox.Group>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={24} md={12}>
            <div
              style={{
                backgroundColor: "#fafafa",
                padding: 16,
                borderRadius: 8,
                border: "1px solid #d9d9d9",
                marginBottom: 16,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Form.Item
                label={
                  <span style={styles.titleInnerControl}>
                    Title
                    <span style={{ color: "red", marginLeft: 4 }}>*</span>
                  </span>
                }
                name="title"
                rules={[
                  { validator: validateTextField("Title", 150, true) },
                ]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  placeholder="Enter Title"
                  disabled={mode === "view" || mode === "views"}
                  onPaste={(e) => {
                    const pastedText = e.clipboardData.getData("text");
                    const currentValue = (e.target as HTMLInputElement).value;
                    if (currentValue.length + pastedText.length > 150) {
                      e.preventDefault(); // prevent paste
                      message.error("Cannot paste more than 150 characters");
                    }
                  }}
                />
              </Form.Item>
            </div>

          </Col>

          <Col xs={24} sm={24} md={12}>
            <div
              style={{
                backgroundColor: "#fafafa",
                padding: 16,
                borderRadius: 8,
                border: "1px solid #d9d9d9",
                marginBottom: 16,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Form.Item
                label={<span style={styles.titleInnerControl}>Subtitle</span>}
                name="subtitle"
                rules={[{ validator: validateTextField("Subtitle", 250, false) }]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  placeholder="Enter Subtitle"
                  disabled={mode === "view" || mode === "views"}
                  onPaste={(e) => {
                    const pastedText = e.clipboardData.getData("text");
                    const currentValue = (e.target as HTMLInputElement).value;
                    if (currentValue.length + pastedText.length > 250) {
                      e.preventDefault();
                      message.error("Cannot paste more than 250 characters");
                    }
                  }}
                />
              </Form.Item>
            </div>

          </Col>
        </Row>

        {selectedTypes.includes("email") && (
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12}>
              <div
                style={{
                  backgroundColor: "#fafafa",
                  padding: 16,
                  borderRadius: 8,
                  border: "1px solid #d9d9d9",
                  marginBottom: 16,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Form.Item
                  label={
                    <span style={styles.titleInnerControl}>
                      Subject Line
                      <span style={{ color: "red", marginLeft: 4 }}>*</span>
                    </span>
                  }
                  name="emailSubject"
                  rules={[{ validator: validateTextField("Email Subject", 150, true) }]}
                  style={{ display: "flex", flexDirection: "column", marginBottom: 0 }}
                >
                  <Input.TextArea
                    placeholder="Enter Email Subject"
                    style={{
                      width: "100%",
                      minHeight: 168,
                      resize: "none",
                    }}
                    disabled={mode === "view" || mode === "views"}
                    onPaste={(e) => {
                      const pastedText = e.clipboardData.getData("text");
                      const currentValue = (e.target as HTMLTextAreaElement).value;
                      if (currentValue.length + pastedText.length > 150) {
                        e.preventDefault(); // prevent paste
                        message.error("Cannot paste more than 150 characters");
                      }
                    }}
                  />
                </Form.Item>
              </div>

            </Col>

            <Col xs={24} sm={24} md={12}>
              <div
                style={{
                  backgroundColor: "#fafafa",
                  padding: 16,
                  borderRadius: 8,
                  border: "1px solid #d9d9d9",
                  marginBottom: 16,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Form.Item
                  label={
                    <span style={styles.titleInnerControl}>
                      Email Text
                      <span style={{ color: "red", marginLeft: 4 }}>*</span>
                    </span>
                  }
                  name="emailText"
                  rules={[{ validator: validateTextField("Email Body", 1500, true) }]}
                  style={{ display: "flex", flexDirection: "column", marginBottom: 0 }}
                >
                  <Input.TextArea
                    rows={6}
                    placeholder="Enter Email Body"
                    style={{
                      width: "100%",
                      minHeight: 168,
                      resize: "none",
                    }}
                    disabled={mode === "view" || mode === "views"}
                    onPaste={(e) => {
                      const pastedText = e.clipboardData.getData("text");
                      const currentValue = (e.target as HTMLTextAreaElement).value;
                      if (currentValue.length + pastedText.length > 1500) {
                        e.preventDefault(); // prevent paste
                        message.error("Cannot paste more than 1500 characters");
                      }
                    }}
                  />
                </Form.Item>
              </div>

            </Col>
          </Row>

        )}

        <Row gutter={[16, 16]} align="top">
          <Col xs={24}>
            <div style={{
              backgroundColor: "#fafafa",
              padding: 16,
              borderRadius: 8,
              border: "1px solid #d9d9d9",
              marginBottom: 16,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}>
              <Form.Item
                label={<span style={styles.titleInnerControl}>Description<span style={{ color: "red", marginLeft: 4 }}>*</span></span>}
                required={true}
                name="description"
                rules={[
                  { validator: validateTextField("Description", 2000, true) },
                ]}
                validateTrigger={["onChange", "onBlur"]}
                style={{ marginBottom: 0 }}
              >
                <CKEditor
                  editor={ClassicEditor as any}
                  config={{
                    placeholder: "Enter notification description",
                    toolbar: [
                      "heading", "|",
                      "bold", "italic", "link", "bulletedList", "numberedList", "|",
                      "blockQuote", "undo", "redo", "imageUpload",
                      "alignment",
                      "imageStyle:alignLeft", "imageStyle:alignCenter", "imageStyle:alignRight",
                    ],
                    image: {
                      toolbar: [
                        "imageStyle:alignLeft",
                        "imageStyle:alignCenter",
                        "imageStyle:alignRight",
                        "imageStyle:full",
                      ],
                      resizeUnit: "%",
                      resizeOptions: [
                        { name: "resizeImage:original", label: "Original", value: null },
                        { name: "resizeImage:25", label: "25%", value: "25" },
                        { name: "resizeImage:50", label: "50%", value: "50" },
                        { name: "resizeImage:75", label: "75%", value: "75" },
                      ],
                    },
                    link: {
                      decorators: {
                        isExternal: {
                          mode: 'manual',
                          label: 'Open in new tab',
                          attributes: {
                            target: '_blank',
                            rel: 'noopener noreferrer'
                          }
                        }
                      }
                    }
                  }}
                  data={editorData}
                  // disabled={mode === "view" || mode === "views"}

                  onReady={(editor) => {
                    //  Store editor instance
                    setEditorInstance(editor);
                    // 1️ Register upload adapter first
                    editor.plugins.get("FileRepository").createUploadAdapter = (loader: any) =>
                      new CustomUploadAdapter(loader);

                    //  Set initial read-only state
                    if (mode === "view" || mode === "views") {
                      editor.enableReadOnlyMode("viewMode");
                    }

                    // 3️ Keep current editor content synced
                    (window as any).currentEditorContent = editorData || "";
                  }}
                  onChange={handleDescriptionChange}
                />

                <style>
                  {`
  .ck-editor__editable_inline {
    min-height: 200px;
    max-height: 400px;
    overflow-y: auto;
    background-color: ${mode === "view" || mode === "views" ? "#f0f0f0" : "#ffffff"};
    color: ${mode === "view" || mode === "views" ? "#6b6969" : "#000000"};
  }

  .ck-content img {
    width: 200px !important;
    height: 200px !important;
    object-fit: contain;
    display: block;
    margin: 10px auto !important;
  }

  /* Allow scrolling but prevent editing */
  ${mode === "view" || mode === "views" ? `
    .ck-toolbar {
      display: none !important;
    }
    .ck-editor__editable {
      pointer-events: auto !important; /* enable scrolling */
      user-select: text !important; /* allow text selection */
      cursor: default !important;
    }
    .ck-editor__editable[contenteditable="true"] {
      pointer-events: auto !important;
      user-select: text !important;
    }
    .ck-editor__editable_inline[contenteditable="true"] {
      pointer-events: auto !important;
    }
    .ck-blurred[contenteditable="true"],
    .ck-focused[contenteditable="true"] {
      pointer-events: auto !important;
    }
    .ck-editor__editable[contenteditable="true"] * {
      pointer-events: auto !important;
    }
    .ck-editor__editable_inline {
      overflow-y: auto !important;
    }
  ` : ""}

  .ck-powered-by,
  .ck-powered-by-ckeditor,
  [class*="ck-powered-by"],
  .ck-editor__main .ck-powered-by {
    display: none !important;
  }
`}
                </style>

              </Form.Item>
            </div>
          </Col>
        </Row>

        <Row gutter={[16, 16]} justify="end" style={{ marginTop: 24 }}>
          <Col xs={24} sm={4} md="auto">
            <Button style={{ width: "100%" }} onClick={openPreview} className="submit-btn">
              Mobile Preview
            </Button>
          </Col>

          {(mode !== "view" && mode !== "views") && (
            <>
              <Col xs={24} sm={4} md="auto">
                <Button style={{ width: "100%" }} className="submit-btn"
                  onClick={() => handleSave(0)}
                >
                  Save as Draft
                </Button>
              </Col>
              <Col xs={24} sm={4} md="auto">
                <Button
                  type="primary"
                  className="submit-btn btn-publish"
                  style={{ width: "100%" }}
                  onClick={async () => {
                    try {
                      //  Trigger form validation for ALL fields
                      await form.validateFields();

                      //  If all fields are valid → open modal
                      setIsPublishModalOpen(true);
                    } catch (error) {
                      // Validation failed → show error message
                      messageApi.error("Please fill all mandatory fields before publishing.");
                    }
                  }}
                >
                  Publish
                </Button>
                <Modal
                  open={isPublishModalOpen}
                  onCancel={() => setIsPublishModalOpen(false)}
                  footer={null}
                  centered
                  closable={false}
                  bodyStyle={{
                    textAlign: "center",
                    padding: "24px 16px",
                    borderRadius: 12,
                  }}
                >
                  <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                    Confirm Publish
                  </h3>
                  <p style={{ fontSize: 14, color: "#555", marginBottom: 24 }}>
                    Are you sure you want to publish this notification?
                  </p>

                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <Button
                      type="primary"
                      block
                      style={{
                        backgroundColor: "#41787C",
                        borderColor: "#41787C",
                        height: 40,
                        fontWeight: 500,
                      }}
                      onClick={() => {
                        setIsPublishModalOpen(false);
                        handleSave(1); //  Publish only when confirmed
                      }}
                    >
                      Yes
                    </Button>

                    <Button
                      block
                      style={{
                        backgroundColor: "#f2f2f2",
                        border: "none",
                        color: "#333",
                        height: 40,
                        fontWeight: 500,
                      }}
                      onClick={() => setIsPublishModalOpen(false)}
                    >
                      No
                    </Button>
                  </div>
                </Modal>
              </Col>
            </>
          )}
        </Row>
      </Form>

      {previewVisible && (
        <>
          {/* Overlay */}
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1000,
            }}
            onClick={() => setPreviewVisible(false)}
          />

          {/* Drawer */}
          <div
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              width: window.innerWidth < 768 ? "100%" : "420px", // full width on mobile
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.4)",
              boxShadow: "-2px 0 8px rgba(0,0,0,0.3)",
              zIndex: 1001,
              padding: "1rem",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <div
              style={{
                maxHeight: "calc(100vh - 140px)",
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <MobileMobilePreviewPage
                data={previewData}
              />
            </div>
            <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
              <Button
                type="primary"
                onClick={() => setPreviewVisible(false)}
                style={{ ...styles.whiteButton, width: 150, color: "black" }}
              >
                Close Preview
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default NotificationCreateMobile;

const baseGreen = '#3E767A';
const styles: { [key: string]: React.CSSProperties } = {

  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '1px solid #d9d9d9',
    background: '#F9F9FA 0% 0% no-repeat padding-box',
    outline: 'none',
  },
  campaignLabel: {
    color: '#3E767A',
    fontSize: '20px',
    marginBottom: '7px',
    fontWeight: 630,
    //textAlign: 'left',
    lineHeight: '24px',
  },
  button: {
    backgroundColor: '#ff4d4f',
    color: 'white',
    border: 'none',
    padding: '10px 15px',
    borderRadius: '4px',
    cursor: 'pointer',
    marginBottom: '20px',
  },

  checkboxGroup: {
    display: "flex",
    gap: "20px",
    alignItems: "center",
    marginBottom: "25px",
  },
  titleInnerControl: {
    color: "#C19763",
    fontWeight: "bold",
    marginBottom: "8px",
    paddingBottom: "4px",
    borderBottom: "2px solid #C19763",
    display: "inline-block",
  },
  whiteButton:
  {
    padding: "8px 24px",
    minWidth: "140px",
    fontSize: '13px',
    borderRadius: '20px',
    fontWeight: 600,
    border: `2px solid #F1F2F4`,
    color: baseGreen,
    cursor: 'pointer',
    //transition: 'all 0.2s ease-in-out',
    backgroundColor: '#F1F2F4',
    //boxShadow: '0 2px 0 #F1F2F4',
    //transform: 'translateY(2px)',
  },
};





